"use client";

import React, { SyntheticEvent, use } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { LoaderCircleIcon } from "lucide-react";
import { isStrongPassword } from "@/lib/utils";
import { useLicenseContext } from "@/contexts/licenseContext";
import { useRouter } from "next/navigation";
import { AxiosError, AxiosResponse } from "axios";
import PasswordRule from "@/app/(license)/[whitelabelId]/(public)/register/_components/passwordRule";
import OverlayLoader from "@/components/overlay-loader";
import { useCallbackRef } from "@/hooks/use-callback-ref";

interface IProps {
	searchParams: Promise<{
		cupom?: string;
		f?: string;
	}>
}

interface Errors {
	name?: string;
	email?: string;
	phone?: string;
	password?: string;
	repeat?: string;
	coupon?: string;
	generic?: string;
}

export default function Page(props: IProps) {
	const searchParams = use(props.searchParams);
	const { whitelabel, setSession, apiClient } = useLicenseContext();
	const router = useRouter();

	const [name, setName] = React.useState("");
	const [email, setEmail] = React.useState("");
	const [password, setPassword] = React.useState("");
	const [repeat, setRepeat] = React.useState("");
	const [phone, setPhone] = React.useState("");
	const [coupon, setCoupon] = React.useState(searchParams.cupom ?? "");
	const [loading, setLoading] = React.useState<boolean>(false);
	const [processing, setProcessing] = React.useState<boolean>(false);
	const [errors, setErrors] = React.useState<Errors>({});

	const handlePhoneChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		const value = event.target.value.replaceAll(/[^0-9+()\-\s]/g, "");

		if (value.length > 20) {
			return;
		}

		setPhone(value);
	}

	const handleRegister = useCallbackRef(async () => {
		if (processing) {
			return;
		}

		setProcessing(true);

		let currentErrors: Errors = {};

		if (coupon) {
			try {
				await apiClient.getCoupon(coupon);
			} catch (e) {
				currentErrors.coupon = "Cupom inválido";
			}
		}

		if (name.trim().length === 0) {
			currentErrors.name = "Informe o nome";
		}

		if (email.trim().length === 0) {
			currentErrors.email = "E-mail não pode ser vazio";
		}

		if (!/^[\w-.]+@([\w-]+\.)+[\w-]{2,4}$/g.test(email)) {
			currentErrors.email = "E-mail inválido";
		}

		if (password.trim().length === 0) {
			currentErrors.password = "Informe a senha";
		}

		const [isStrong, message] = isStrongPassword(password);

		if (!isStrong) {
			currentErrors.password = message;
		}

		if (repeat.trim().length === 0) {
			currentErrors.repeat = "Repita a senha";
		}

		if (password !== repeat) {
			currentErrors.repeat = "As senhas não conferem";
		}

		setErrors(currentErrors);

		if (Object.entries(currentErrors).length > 0) {
			setProcessing(false);
			return;
		}

		let response: AxiosResponse<ILicenseSession>;

		try {
			response = await apiClient.register({
				name: name,
				email: email,
				password: password,
				phone: phone,
				couponCode: coupon,
				externalId: searchParams.f,
			});
		} catch (error) {
			if (error instanceof AxiosError) {
				const response = error.response as AxiosResponse;
				if (response.data.errors) {
					currentErrors = response.data.errors as Errors;
				} else {
					currentErrors.generic = response.data.message ?? 'Erro ao cadastrar usuário';
				}
			} else {
				currentErrors.generic = 'Erro ao cadastrar usuário';
			}

			setErrors(currentErrors);
			return;
		} finally {
			setProcessing(false);
		}

		setSession(response.data);
		toast.success("Usuário cadastrado com sucesso");

		let to = `/${whitelabel.identifier}/plugin`;

		router.push(to);
	});

	const submit = React.useCallback(async (event: SyntheticEvent<HTMLFormElement, SubmitEvent>) => {
		event.preventDefault();

		setLoading(true);

		try {
			await handleRegister();
		} catch (e) {
		}

		setLoading(false)
	}, [handleRegister]);

	return (
		<form onSubmit={submit}>
			<OverlayLoader loading={loading} overlayClass="rounded-lg">
				<Card className="w-screen md:w-[600px]">
					<CardHeader className="text-center text-balance">
						<CardTitle>Cadastrar</CardTitle>
						<CardDescription>Informe seus dados para criação da conta</CardDescription>
					</CardHeader>
					<CardContent>
						<div className="grid grid-cols-1 md:grid-cols-2 items-start gap-4">
							<div className="grid col-span-full gap-2">
								<Label htmlFor="name">Nome Completo</Label>
								<Input
									id="name"
									placeholder="Nome Completo"
									required
									value={name}
									onChange={(e) => setName(e.target.value)}
								/>
								{ errors.name && (
									<div className="text-xs text-red-700">{errors.name}</div>
								)}
							</div>
							<div className="grid gap-2">
								<Label htmlFor="email">E-mail</Label>
								<Input
									id="email"
									type="email"
									placeholder="email@example.com"
									required
									value={email}
									onChange={(e) => setEmail(e.target.value)}
								/>
								{ errors.email && (
									<div className="text-xs text-red-700">{errors.email}</div>
								)}
							</div>
							<div className="grid gap-2">
								<Label htmlFor="phone">Telefone</Label>
								<Input
									id="phone"
									type="text"
									placeholder="00 0000-0000"
									required
									value={phone}
									onChange={handlePhoneChange}
								/>
								{ errors.phone && (
									<div className="text-xs text-red-700">{errors.phone}</div>
								)}
							</div>
							<div className="grid gap-2">
								<Label htmlFor="password">Senha</Label>
								<Input
									id="password"
									type="password"
									value={password}
									required
									onChange={(e) => setPassword(e.target.value)}
								/>
								{ errors.password && (
									<div className="text-xs text-red-700">{errors.password}</div>
								)}
							</div>

							<div className="grid gap-2">
									<div className="flex flex-col gap-2 text-muted-foreground text-xs">
										<PasswordRule isValid={password.length >= 8} >
											Possui pelo menos 8 caracteres
										</PasswordRule>
										<PasswordRule isValid={/[a-zA-Z]/.test(password)} >
											Possui pelo menos uma letra
										</PasswordRule>
										<PasswordRule isValid={/[0-9]/.test(password)} >
											Possui pelo menos um número
										</PasswordRule>
									</div>
							</div>

							<div className="grid gap-2">
								<Label htmlFor="repeat-password">Repita a senha</Label>
								<Input
									id="repeat-password"
									type="password"
									required
									value={repeat}
									onChange={(e) => setRepeat(e.target.value)}
								/>
								{ errors.repeat && (
									<div className="text-xs text-red-700">{errors.repeat}</div>
								)}
							</div>

							<div className="grid gap-2">
								<Label htmlFor="cupom">Possui algum cupom?</Label>
								<Input
									id="cupom"
									placeholder="Cupom"
									value={coupon}
									onChange={(e) => setCoupon(e.target.value)}
								/>
								{ errors.coupon && (
									<div className="text-xs text-red-700">{errors.coupon}</div>
								)}
							</div>

						</div>
					</CardContent>
					<CardFooter className="flex flex-col gap-2">
						<div>
							{ errors.generic && (
								<div className="text-xs text-red-700">{errors.generic}</div>
							)}
						</div>

						<Button
							className="w-full flex gap-2"
							variant="success"
							type="submit"
							disabled={processing}
						>
							{processing && (<LoaderCircleIcon className="size-4 animate-spin" />)}
							Registrar-se
						</Button>

					<div className="text-center">
							Já possui conta?

							<a href={`/${whitelabel.identifier}/login`}>
								<Button
									className="px-2 underline underline-offset-1"
									type="button"
									variant="link">
									Faça login
								</Button>
							</a>
						</div>

					</CardFooter>
				</Card>
			</OverlayLoader>
		</form>
	);
}

